import { baseApi } from './api';
import {
    NapiResultType,
    Device,
    VirtualDeviceInputFillup,
    MappedVirtualDeviceInput,
    DeviceHealth,
    DeviceDetailedHealth,
    DevicePrediction,
    DeviceReadingsDictionaryResult,
    DeviceConsumptionResult,
    BackOfficeDeliveryIconConfig,
} from 'types';
import { StoredUser } from '../features/authentication';
import { AlertsConfig } from 'views/DeviceDetails/views/DeviceAlertsConfig/DeviceAlertsConfig';
import { DefaultEsQueryConfig } from 'lib/constants';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import {
    getEsDeviceSearchJsonQuery,
    getMapViewJsonQuery,
    getTankTableSortField,
} from './es-query-helpers';
import { isDeviceConnected } from './device-status';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import {
    getBackOfficeDeliveryIcon,
    getBackOfficeSyncIcon,
} from './third-party-backoffice';
import { getTodayDd } from 'helpers/dates';
import { isDeviceOffline } from '../../helpers/device-offline';
import _ from 'lodash';

// Define a service using a base URL and expected endpoints
export const deviceApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Get all of the supplier's devices from Elasticsearch.
         * @param {string | null} userUid uid of logged in user.
         * @param {string} supplierId id of supplier to get devices for.
         * @param {ElasticsearchDeviceBodyConfig} queryConfig options for ES search.
         */
        getSupplierDevicesEs: builder.query<
            NapiResultType<Device[]>,
            {
                userUid: string | null;
                supplierId: string;
                queryConfig?: ElasticsearchDeviceBodyConfig;
            }
        >({
            query: ({ userUid, supplierId, queryConfig }) => {
                if (!userUid) throw new Error('userUid is required.');
                if (!supplierId.toString()) {
                    throw new Error('supplierId is required.');
                }

                return {
                    method: 'POST',
                    url: 'device-search',
                    body: {
                        json_query: getEsDeviceSearchJsonQuery(
                            supplierId.toString(),
                            queryConfig
                        ),
                        limit: queryConfig?.limit ?? DefaultEsQueryConfig.limit,
                        offset:
                            queryConfig?.offset ?? DefaultEsQueryConfig.offset,
                        dir:
                            queryConfig?.direction ??
                            DefaultEsQueryConfig.direction,
                        sort:
                            getTankTableSortField(
                                queryConfig?.sortField ?? ''
                            ) ?? DefaultEsQueryConfig.sortField,
                        uid: userUid,
                        provider: 'es',
                    },
                };
            },
            transformResponse: (response: NapiResultType<Device[]>) => {
                const results: Device[] = [];
                response.results.forEach((device: Device) => {
                    isDeviceConnected(device);
                    computeDeviceHealth(device);
                    results.push(device);
                });

                return {
                    aggregations: response.aggregations,
                    total: response.total,
                    results: results,
                };
            },
        }),
        /** Get a device based on (long) device id. Uses napi's non-ES querying. */
        getDeviceByLongIdNapi: builder.query<Device, { longDeviceId: string }>({
            query: ({ longDeviceId }) => {
                if (!longDeviceId) {
                    throw new Error('longDeviceId is required.');
                }
                return {
                    method: 'GET',
                    url: `devices/${longDeviceId}`,
                };
            },
            transformResponse: (response: any) => {
                const predictions = transformPredictionData(response);
                if (response?.lastPrediction?.predictions)
                    response.lastPrediction.predictions = predictions;

                const syncConfig = getBackOfficeSyncIcon({ ...response });

                response._bo_sync_complete = syncConfig.bo_sync_complete;
                response._backoffice_icon = syncConfig.backoffice_icon;
                response._backoffice_style = syncConfig.backoffice_style;
                response._bo_sync_msg = syncConfig.bo_sync_msg;

                // Get latest backoffice delivery icon data.
                const newIconConfig: BackOfficeDeliveryIconConfig =
                    getBackOfficeDeliveryIcon({
                        ...response,
                    });
                response._delivery_icon = newIconConfig.delivery_icon;
                response._delivery_style = newIconConfig.delivery_style;
                response._bo_delivery_msg = newIconConfig.bo_delivery_msg;

                return { ...response };
            },
        }),
        // TODO: we should use the getDeviceByLongIdNapi or getDeviceByShortIdNapi (which doesn't currently exist) when looking up
        // TODO: devices one by one. It costs more to do ES lookups than FB lookups. This query should probably be removed.
        /** Query Elasticsearch for a device based on short device id. */
        getDeviceByShortIdEs: builder.query<
            NapiResultType<Device[]>,
            { user: StoredUser | null; shortDeviceId: string }
        >({
            query: ({ user, shortDeviceId }) => ({
                method: 'POST',
                url: 'device-search',
                body: {
                    json_query: {
                        query: {
                            bool: {
                                filter: {
                                    match: {
                                        shortDeviceId,
                                    },
                                },
                            },
                        },
                    },
                    limit: 1,
                    offset: 0,
                    dir: 'ASC',
                    uid: user?.uid,
                    provider: 'es',
                },
            }),
        }),
        // NOTE: use the Napi query when getting only one device. This is only useful when querying multiple IDs at once since elastic-search is more expensive than napi
        /** Query Elasticsearch for a device based on (long) device id. */
        getDeviceByDeviceIdEs: builder.query<
            NapiResultType<Device[]>,
            { user: StoredUser | null; deviceId: string; limit?: number }
        >({
            query: ({ user, deviceId, limit = 1 }) => ({
                method: 'POST',
                url: 'device-search',
                body: {
                    json_query: {
                        query: {
                            bool: {
                                filter: {
                                    match: {
                                        deviceId,
                                    },
                                },
                            },
                        },
                    },
                    limit,
                    offset: 0,
                    dir: 'ASC',
                    uid: user?.uid,
                    provider: 'es',
                },
            }),
        }),
        getDeviceHealth: builder.query<
            DeviceDetailedHealth,
            { deviceId: string }
        >({
            query: ({ deviceId }) => {
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }
                return { method: 'GET', url: `devices/${deviceId}/health` };
            },
        }),
        cloneDevice: builder.query<
            Device,
            {
                cloneToDeviceId: string;
                cloneFromDeviceId: string;
                opts: { includeReadings: boolean; reading_start: number };
            }
        >({
            query: ({ cloneToDeviceId, cloneFromDeviceId, opts }) => {
                if (!cloneToDeviceId) {
                    throw new Error('cloneToDeviceId is required.');
                }
                if (!cloneFromDeviceId) {
                    throw new Error('cloneFromDeviceId is required.');
                }
                const data = {
                    from: cloneFromDeviceId,
                    to: cloneToDeviceId,
                    opts,
                };
                return {
                    method: 'POST',
                    url: 'devices/clone',
                    body: data,
                };
            },
        }),
        getDeviceReadings: builder.query<
            DeviceReadingsDictionaryResult,
            { deviceId: string; start?: string; end?: string }
        >({
            query: ({ deviceId, start, end }) => {
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }
                return {
                    method: 'GET',
                    url: `devices/${deviceId}/readings`,
                    params: {
                        ...(start && { start }),
                        ...(end && { end }),
                    },
                };
            },
        }),
        getDeviceTicket: builder.query<
            Ticket,
            {
                deviceId: string;
                supplierId: string;
                ticketId?: string;
                ticketCategory: string;
                ticketDirectory: string;
            }
        >({
            query: ({
                deviceId,
                supplierId,
                ticketId,
                ticketCategory,
                ticketDirectory,
            }) => {
                return {
                    method: 'GET',
                    url: `tickets/${supplierId}/${ticketCategory}/${ticketDirectory}/${deviceId}`,
                    params: {
                        id: ticketId,
                    },
                };
            },
        }),
        getDeviceConsumption: builder.query<
            DeviceConsumptionResult,
            { deviceId: string; start: string; end: string }
        >({
            query: ({ deviceId, start, end }) => {
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }
                return {
                    method: 'GET',
                    url: `devices/${deviceId}/consumption?start=${start}&end=${end}`,
                };
            },
        }),
        createVirtualDevice: builder.query<
            { device: Device },
            {
                device: MappedVirtualDeviceInput;
                fillups: VirtualDeviceInputFillup[];
            }
        >({
            query: ({ device, fillups }) => ({
                method: 'POST',
                url: 'devices/create-virtual-device',
                body: { device, fillups },
            }),
        }),
        patchUpdateDevice: builder.query<
            Device,
            { deviceId: string; updatedFields: Partial<Device> }
        >({
            query: ({ deviceId, updatedFields }) => {
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }

                return {
                    method: 'PATCH',
                    url: `devices/${deviceId}`,
                    body: updatedFields,
                };
            },
            transformResponse: (response: any) => {
                const predictions = transformPredictionData(response);
                const syncConfig = getBackOfficeSyncIcon({ ...response });

                response._bo_sync_complete = syncConfig.bo_sync_complete;
                response._backoffice_icon = syncConfig.backoffice_icon;
                response._backoffice_style = syncConfig.backoffice_style;
                response._bo_sync_msg = syncConfig.bo_sync_msg;

                if (response?.lastPrediction?.predictions)
                    response.lastPrediction.predictions = predictions;
                return { ...response };
            },
        }),
        patchUpdateDeviceAlerts: builder.query<
            AlertsConfig,
            {
                deviceId: string;
                supplierId: string;
                updatedFields: Partial<AlertsConfig>;
            }
        >({
            query: ({ deviceId, supplierId, updatedFields }) => ({
                method: 'PATCH',
                url: `alerts/${deviceId}/${supplierId}`,
                body: updatedFields,
            }),
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
        /**Admin users only. Endpoint does the following: 1. Add to device history. 2. Deatch supplier. 3. Remove users. 4. Clear alerts, consumption, status. */
        adminRemoveSupplierFromDevice: builder.mutation<
            any,
            { deviceId: string }
        >({
            query: ({ deviceId }) => {
                return {
                    method: 'DELETE',
                    url: `/devices/${deviceId}/clear-owners`,
                };
            },
        }),
        /** Admin users only. Update the status of a device. */
        adminPatchDeviceStatus: builder.mutation<
            any,
            { deviceId: string; newStatus: string }
        >({
            query: ({ deviceId, newStatus }) => {
                return {
                    method: 'PATCH',
                    url: `/devices/${deviceId}/status`,
                    body: { status: newStatus },
                };
            },
        }),
        /** Admin users only. Update the supplier id on a device. */
        adminPatchDeviceSupplierId: builder.mutation<
            any,
            { deviceId: string; newSupplierId: string }
        >({
            query: ({ deviceId, newSupplierId }) => {
                return {
                    method: 'PATCH',
                    url: `/devices/${deviceId}/supplier-id`,
                    body: { supplierId: newSupplierId },
                };
            },
        }),
        /**
         * Get all of the supplier's devices from Elasticsearch for Map View.
         * @param {string | null} userUid uid of logged in user.
         * @param {string} supplierId id of supplier to get devices for.
         * @param {ElasticsearchDeviceBodyConfig} queryConfig options for ES search.
         */
        getSupplierDevicesMapView: builder.query<
            NapiResultType<Device[]>,
            {
                userUid: string | null;
                supplierId: string;
                queryConfig: ElasticsearchDeviceBodyConfig;
            }
        >({
            query: ({ userUid, supplierId, queryConfig }) => ({
                method: 'POST',
                url: 'device-search',
                body: {
                    json_query: getMapViewJsonQuery(supplierId, queryConfig),
                    limit: queryConfig?.limit ?? DefaultEsQueryConfig.limit,
                    offset: queryConfig?.offset ?? DefaultEsQueryConfig.offset,
                    dir:
                        queryConfig?.direction ??
                        DefaultEsQueryConfig.direction,
                    sort:
                        getTankTableSortField(queryConfig?.sortField ?? '') ??
                        DefaultEsQueryConfig.sortField,
                    uid: userUid,
                    provider: 'es',
                },
            }),
            transformResponse: (response: NapiResultType<Device[]>) => {
                const results: Device[] = [];
                response.results.forEach((device: Device) => {
                    isDeviceConnected(device);
                    transformEstimatedTank(device);
                    results.push(device);
                });

                return {
                    aggregations: response.aggregations,
                    total: response.total,
                    results: results,
                };
            },
        }),
    }),
});

/** Compute the device.health field and add/update the device.health data for the inputted `device`. */
export const computeDeviceHealth = (device: Device): DeviceHealth => {
    // Default device to healthy.
    device.health = { tooltip: 'Healthy', color: '#31a354', icon: 'Lens' };

    const lr = _.get(device, 'lastReading');

    // If last reading exists, check if device is offline.
    if (lr && lr.time) {
        // If offline, change device health
        if (isDeviceOffline(device)) {
            device.health = {
                tooltip: 'Offline',
                color: '#FF5722',
                icon: 'CloudOff',
            };
        }

        // If device does not have a last reading, mark as not configured.
    } else {
        device.health = {
            tooltip: 'Not Configured',
            color: '#FF5722',
            icon: 'Error',
        };
    }

    if (device.status === 'sleep') {
        device.health = {
            tooltip: 'Set to sleep',
            color: '#f9a825',
            icon: 'Snooze',
        };
    }
    if (device.connection_type === 'virtual') {
        device.health = {
            tooltip: 'Virtual Monitor',
            color: '#31a354',
            icon: 'Laptop',
        };
    }

    return device.health;
};

const transformPredictionData = (response: any): DevicePrediction[] => {
    const predictions: DevicePrediction[] = [];
    if (response?.lastPrediction?.predictions) {
        // NAPI returns predictions in an object of dd values mapped to prediction objects. Need to transform that
        // to an array of DevicePrediction types.
        Object.entries(response.lastPrediction.predictions).forEach(
            (entry: any) => {
                const [dd, rawPrediction] = entry;
                predictions.push({
                    dd: dd,
                    ...rawPrediction,
                });
            }
        );
    }

    return predictions;
};

const transformEstimatedTank = (device: Device) => {
    if (!device.lastReading) {
        return;
    }

    const todayDd = parseInt(getTodayDd());
    let estimated_tank = device.lastReading.tank;

    if (
        device.lastPrediction &&
        Array.isArray(device.lastPrediction.predictions)
    ) {
        const todayEstimate = device.lastPrediction.predictions.find(
            (prediction) => prediction.dd === todayDd
        );

        if (todayEstimate) {
            let estimated_tank_value = parseFloat(
                todayEstimate.tank.toString()
            );

            if (estimated_tank_value < 0) {
                estimated_tank_value = 0;
            }

            estimated_tank = estimated_tank_value;
        }
    }
    return (device.estimated_tank = estimated_tank);
};

export const {
    useGetSupplierDevicesEsQuery,
    useLazyGetSupplierDevicesEsQuery,
    useGetDeviceByShortIdEsQuery,
    useLazyGetDeviceByShortIdEsQuery,
    useGetDeviceByDeviceIdEsQuery,
    useLazyGetDeviceByDeviceIdEsQuery,
    useLazyGetDeviceHealthQuery,
    useLazyGetDeviceReadingsQuery,
    useLazyGetDeviceConsumptionQuery,
    useLazyCreateVirtualDeviceQuery,
    useLazyGetDeviceByLongIdNapiQuery,
    useLazyPatchUpdateDeviceQuery,
    useLazyPatchUpdateDeviceAlertsQuery,
    useAdminRemoveSupplierFromDeviceMutation,
    useAdminPatchDeviceStatusMutation,
    useAdminPatchDeviceSupplierIdMutation,
    useLazyGetDeviceTicketQuery,
    useGetSupplierDevicesMapViewQuery,
    useLazyGetSupplierDevicesMapViewQuery,
    useLazyCloneDeviceQuery,
} = deviceApi;
